/**
 * AI Conversation Types
 *
 * Portable domain types for the agent conversation system: conversations,
 * toolcalls, invocations, tool-facing metadata, and model registry.
 */

import { z } from 'zod';

export type AiModelEntry = {
  provider: string;
  modelId: string;
  label: string;
  group: string;
  pricing: { inputPerM: number; outputPerM: number };
};

export const AI_MODELS: AiModelEntry[] = [
  { provider: 'anthropic', modelId: 'claude-haiku-4.5', label: 'Claude Haiku 4.5', group: 'Anthropic', pricing: { inputPerM: 1.00, outputPerM: 5.00 } },
  { provider: 'anthropic', modelId: 'claude-sonnet-4.5', label: 'Claude Sonnet 4.5', group: 'Anthropic', pricing: { inputPerM: 3.00, outputPerM: 15.00 } },
  { provider: 'anthropic', modelId: 'claude-sonnet-4.6', label: 'Claude Sonnet 4.6', group: 'Anthropic', pricing: { inputPerM: 3.00, outputPerM: 15.00 } },
  { provider: 'anthropic', modelId: 'claude-opus-4.5', label: 'Claude Opus 4.5', group: 'Anthropic', pricing: { inputPerM: 5.00, outputPerM: 25.00 } },
  { provider: 'anthropic', modelId: 'claude-opus-4.6', label: 'Claude Opus 4.6', group: 'Anthropic', pricing: { inputPerM: 5.00, outputPerM: 25.00 } },
  { provider: 'google', modelId: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', group: 'Google', pricing: { inputPerM: 0.10, outputPerM: 0.40 } },
  { provider: 'google', modelId: 'gemini-3-flash', label: 'Gemini 3 Flash', group: 'Google', pricing: { inputPerM: 0.50, outputPerM: 3.00 } },
  { provider: 'google', modelId: 'gemini-3-pro-preview', label: 'Gemini 3 Pro', group: 'Google', pricing: { inputPerM: 2.00, outputPerM: 12.00 } },
  { provider: 'google', modelId: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', group: 'Google', pricing: { inputPerM: 2.00, outputPerM: 12.00 } },
  { provider: 'google', modelId: 'gemini-3.1-flash-lite-preview', label: 'Gemini 3.1 Flash Lite', group: 'Google', pricing: { inputPerM: 0.25, outputPerM: 1.50 } },
  { provider: 'openai', modelId: 'codex-mini', label: 'Codex Mini', group: 'OpenAI', pricing: { inputPerM: 1.50, outputPerM: 6.00 } },
  { provider: 'openai', modelId: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', group: 'OpenAI', pricing: { inputPerM: 0.40, outputPerM: 1.60 } },
  { provider: 'openai', modelId: 'gpt-5-mini', label: 'GPT-5 Mini', group: 'OpenAI', pricing: { inputPerM: 0.25, outputPerM: 2.00 } },
  { provider: 'openai', modelId: 'gpt-5', label: 'GPT-5', group: 'OpenAI', pricing: { inputPerM: 1.25, outputPerM: 10.00 } },
  { provider: 'deepseek', modelId: 'deepseek-v3.2', label: 'DeepSeek V3.2', group: 'DeepSeek', pricing: { inputPerM: 0.28, outputPerM: 0.42 } },
];

export const DEFAULT_AI_MODEL = AI_MODELS[0]!;
const AI_MODEL_KEYS = AI_MODELS.map((m) => `${m.provider}/${m.modelId}`) as [string, ...string[]];
export const AiModelKeySchema = z.enum(AI_MODEL_KEYS);
export type AiModelKey = z.infer<typeof AiModelKeySchema>;
export function aiModelKey(m: AiModelEntry): AiModelKey {
  return `${m.provider}/${m.modelId}` as AiModelKey;
}
export function findAiModelByKey(key: string): AiModelEntry | undefined {
  return AI_MODELS.find((m) => aiModelKey(m) === key);
}
export const DEFAULT_AI_MODEL_KEY = aiModelKey(DEFAULT_AI_MODEL);
export function calcModelCostUsd(model: AiModelEntry, inputTokens: number, outputTokens: number): number {
  return (
    (inputTokens / 1_000_000) * model.pricing.inputPerM +
    (outputTokens / 1_000_000) * model.pricing.outputPerM
  );
}

export type ConversationType = string;
export type MessageRole = 'user' | 'assistant' | 'system' | 'tool';
export type EntityType = string;

export type TopicSegment = {
  topic: string;
  fromMessageIndex: number;
  toMessageIndex: number;
  summary: string;
};

export type ToolSummary = {
  toolName: string;
  toolCallId: string;
  summary: string;
  messageIndex: number;
};

export type SessionContext = {
  currentIntent: string;
  topics: TopicSegment[];
  recentDirectives: string[];
  keyToolResults: ToolSummary[];
  draftState: string;
  summarizedUpToIndex: number;
};

export type ConversationMemoryStatus = 'inactive' | 'updating' | 'active' | 'unavailable';

export type ConversationBase = {
  conversationId: string;
  createdBy: string;
  conversationType: ConversationType;
  initialPrompt: string;
  metadata: Record<string, unknown> | null;
  modelProvider: string;
  modelId: string;
  createdAt: string;
  updatedAt: string;
  sessionContext?: SessionContext | null;
};

export type ToolcallStatus = 'pending' | 'success' | 'error' | 'blocked' | 'cancelled';

export type AgentConversationMessage = {
  messageId: string;
  conversationId: string;
  messageIndex: number;
  role: MessageRole;
  content: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export type AgentConversationEntity = {
  conversationId: string;
  entityType: EntityType;
  entityId: string;
  createdAt: string;
};

export type ConversationThreadMessage = Pick<
  AgentConversationMessage,
  'messageId' | 'role' | 'content' | 'metadata'
>;

export type ConversationRuntimeMessage = Pick<
  AgentConversationMessage,
  'messageId' | 'messageIndex' | 'role' | 'content' | 'metadata'
>;

export type AgentConversationToolcall = {
  toolcallId: string;
  conversationId: string;
  invocationId: string;
  assistantMessageId: string | null;
  toolCallIndex: number;
  providerToolCallId: string | null;
  toolName: string;
  input: unknown;
  output: unknown | null;
  status: ToolcallStatus;
  errorCode: string | null;
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ConversationThreadToolcall = Pick<
  AgentConversationToolcall,
  | 'toolcallId'
  | 'assistantMessageId'
  | 'toolCallIndex'
  | 'providerToolCallId'
  | 'toolName'
  | 'input'
  | 'output'
  | 'status'
  | 'errorCode'
  | 'errorMessage'
>;

export type ConversationRuntimeToolcall = ConversationThreadToolcall & Pick<
  AgentConversationToolcall,
  | 'conversationId'
  | 'invocationId'
  | 'metadata'
  | 'startedAt'
  | 'completedAt'
  | 'createdAt'
  | 'updatedAt'
>;

export type ConversationThread = ConversationBase & {
  messages: ConversationThreadMessage[];
  toolcalls: ConversationThreadToolcall[];
};

export type ConversationRuntime = ConversationBase & {
  messages: ConversationRuntimeMessage[];
  toolcalls: ConversationRuntimeToolcall[];
};

export type ConversationHistoryRole = Extract<MessageRole, 'user' | 'assistant'>;

export type ConversationHistoryToolcall = Pick<
  AgentConversationToolcall,
  | 'toolcallId'
  | 'toolName'
  | 'toolCallIndex'
  | 'providerToolCallId'
  | 'status'
  | 'input'
  | 'output'
  | 'errorCode'
  | 'errorMessage'
>;

export type ConversationHistoryMessage = {
  messageId: string;
  messageIndex: number;
  role: ConversationHistoryRole;
  content: string | null;
  createdAt: string;
  toolcalls?: ConversationHistoryToolcall[];
};

export type ConversationHistoryRange = {
  fromMessageIndex: number;
  toMessageIndex: number;
  messages: ConversationHistoryMessage[];
};

export type AgentConversationInvocation = {
  invocationId: string;
  conversationId: string;
  userMessageId: string;
  modelProvider: string;
  modelId: string;
  systemPrompt: string;
  messagesSnapshot: unknown[];
  historyMessageIndices: number[] | null;
  sessionContextActive: boolean;
  sessionContextSnapshot: SessionContext | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export type InvocationSnapshot = {
  systemPrompt: string;
  messagesSnapshot: unknown[];
  historyMessageIndices: number[];
  sessionContextActive: boolean;
  sessionContextSnapshot: SessionContext | null;
};

export type CreateConversationInput = {
  conversationType: ConversationType;
  initialPrompt: string;
  modelProvider: string;
  modelId: string;
  metadata?: Record<string, unknown>;
};

export type SaveMessageInput = {
  conversationId: string;
  role: MessageRole;
  content?: string | null;
  metadata?: Record<string, unknown> | null;
  systemPrompt?: string | null;
  prevMessages?: unknown[] | null;
};

export type LinkConversationEntityInput = {
  conversationId: string;
  entityType: EntityType;
  entityId: string;
};
